import type {WorkstreamNavNode} from 'client';
import {DSYS_TOUCH_TARGET_PX} from 'components/DSYS/layoutConstants';

/** A rail row's workstream (or synthetic group header), from the nav tree. */
export type WsNode = {
  id: number;
  name: string;
  pillar?: string | null;
  // Parent workstream id in the narrative hierarchy (All view). null/undefined =
  // attaches directly under its pillar group.
  parentId?: number | null;
  // Every parent in the narrative hierarchy (a workstream can roll up into
  // several). Empty/undefined = only `parentId` is known.
  parentIds?: number[];
  // Its sub-workstreams in their saved order (All view), from the nav tree.
  childIds?: number[];
  latestStatus?: string;
  // True for synthetic pillar-group headers (All view grouping).
  isPillarGroup?: boolean;
  // my_workstreams nav nodes only: the viewer's role on it (see myRoleGroups).
  myRole?: string | null;
  // Admin-set narrative doc link (Pillars admin), shown at the row's end.
  narrativeUrl?: string | null;
  // Its linked product roadmap doc, shown after the narrative as "Roadmap".
  roadmapUrl?: string | null;
  // A recent person's row: their avatar where a workstream's status dot goes.
  person?: {ldap: string; name: string; profile_photo_url?: string | null};
};

/** A fully-materialized tree node. Every view is rendered from these (no lazy fetches). */
export type TreeItem = {
  node: WsNode;
  level: number; // 1 = pillar, 2 = narrative, 3 = sub, 4+ = deeper
  children: TreeItem[];
  autoExpand?: boolean; // starts open
  tooltip?: string; // a group's explainer, in a Toggletip after its label
  badge?: number; // a group's alert count (e.g. updates you owe), after its label
  nestedGroup?: boolean; // a group inside a group that steps in a level (My's role and Enablement groups)
};

// Rail rows (and their chevrons) in the narrow screens' sheet: a finger-sized target.
export const TOUCH_TARGET_PX = DSYS_TOUCH_TARGET_PX;

/** Map a lightweight nav-tree node (from `/prototype/nav-tree`) into the render `WsNode`. */
export const navNodeToWsNode = (n: WorkstreamNavNode): WsNode => ({
  id: n.id,
  name: n.name,
  pillar: n.pillar ?? null,
  parentId: n.parent_id ?? null,
  parentIds: n.parent_ids ?? [],
  childIds: n.child_ids ?? [],
  latestStatus: n.status ?? undefined,
  myRole: n.my_role ?? null,
  narrativeUrl: n.narrative_url ?? null,
  roadmapUrl: n.roadmap_url ?? null,
});
